import { useEffect, useMemo, useState } from 'react';
import { ensureSealedTagsUnsealed, getSealedTagLabelMap } from '../services/memoTagSealService';
import { collectSealedTagTokens } from '../utils/classRecordMemoHashtags';
import { peekSealedTagLabel } from '../utils/memoTagLabelCache';

/**
 * 메모 텍스트들에 `#e:` 가 있으면 1회 배치 복호화해 라벨 맵을 반환.
 * 없으면 RPC를 호출하지 않는다.
 */
export function useSealedMemoTagLabels(texts: Array<string | null | undefined>): Record<string, string> {
  const joined = texts.map((t) => t ?? '').join('\n\u001e');
  const fingerprint = useMemo(() => {
    const tokens: string[] = [];
    const seen = new Set<string>();
    for (const tok of collectSealedTagTokens(joined)) {
      if (seen.has(tok)) continue;
      seen.add(tok);
      tokens.push(tok);
    }
    return tokens.sort().join('\0');
  }, [joined]);

  const [map, setMap] = useState<Record<string, string>>(() => getSealedTagLabelMap());

  useEffect(() => {
    if (!fingerprint) {
      setMap(getSealedTagLabelMap());
      return;
    }
    let cancelled = false;
    const tokens = fingerprint.split('\0').filter(Boolean);
    const already = tokens.every((t) => !!peekSealedTagLabel(t));
    if (already) {
      setMap(getSealedTagLabelMap());
      return;
    }
    void ensureSealedTagsUnsealed(tokens)
      .then((next) => {
        if (!cancelled) setMap({ ...next });
      })
      .catch((err) => {
        console.error('봉인 해시태그 해제 실패:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [fingerprint]);

  return map;
}
